import React, { useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import MuiButton from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import SearchRounded from '@mui/icons-material/SearchRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import Search from '@mui/icons-material/Search';
import Close from '@mui/icons-material/Close';
import StudentAvatar from './StudentPointAvatar';
import TeacherAppreciationCelebration from './TeacherAppreciationCelebration';
import TeacherBonusCelebration from './TeacherBonusCelebration';
import { prepareAppreciationSound, playAppreciationSound } from './teacherAppreciationSound';
import { prepareBonusSound, playBonusSound } from './teacherBonusSound';
import { lockAward, unlockAward, readPendingAward, savePendingAward, verifyAwardResponse } from './teacherPointOperations';
import './TeacherPointAwards.css';

// A visible outline supplies keyboard feedback without a pale ripple covering the label.
const Button = React.forwardRef((props, ref) => <MuiButton ref={ref} disableRipple {...props} />);

// Initial modes are mount-only: home shortcuts mount once after their shared roster loads.
// Existing points/account launchers retain their default behavior.
export default function TeacherPointAwards({ api, students, appreciationStudent, onAppreciationClose, onAward, boardProfile = true,
  hideLauncher = false, initialBonusOpen = false, initialAppreciationOpen = false, onFlowClose }) {
  const [pending, setPending] = useState(() => readPendingAward(api.storageKey));
  const [open, setOpen] = useState(() => initialBonusOpen || initialAppreciationOpen || Boolean(readPendingAward(api.storageKey)));
  const [kind, setKind] = useState(initialAppreciationOpen ? 'teacher_appreciation' : 'teacher_bonus');
  const [ids, setIds] = useState([]);
  const [amount, setAmount] = useState(1);
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInput = useRef(null);
  const searchToggle = useRef(null);
  const [review, setReview] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [submittingSelection, setSubmittingSelection] = useState(false);
  const [success, setSuccess] = useState(null);
  const [legacyRejected, setLegacyRejected] = useState(false);
  const locked = useRef(false);
  const appreciation = (pending?.kind || kind) === 'teacher_appreciation';
  const modernBonusSuccess = success?.kind === 'teacher_bonus' && [1, 2].includes(success.amount);
  const chosenIds = pending?.student_ids || ids;
  const chosen = chosenIds.map(id => students.find(s => s.student_id === id) || { student_id: id, first_name: 'Öğrenci', last_name: `#${id}` });
  const points = pending?.amount || (appreciation ? 3 : amount);
  const label = appreciation ? 'Öğretmen Takdiri' : 'Öğretmen Bonusu';
  const name = (student) => `${student.first_name} ${student.last_name}`.trim();
  const quickAppreciation = initialAppreciationOpen && !appreciationStudent;
  const selecting = !review && (!pending || submittingSelection) && (!appreciation || quickAppreciation);
  const SearchIcon = boardProfile ? SearchRounded : Search;
  const CloseIcon = boardProfile ? CloseRounded : Close;

  useEffect(() => { if (searchOpen && selecting) searchInput.current?.focus(); }, [searchOpen, selecting]);
  useEffect(() => {
    if (success?.kind === 'teacher_appreciation') return playAppreciationSound();
    if (success?.kind === 'teacher_bonus' && [1, 2].includes(success.amount)) return playBonusSound();
    return undefined;
  }, [success]);
  const closeSearch = () => { setSearchOpen(false); setQuery(''); searchToggle.current?.focus(); };

  useEffect(() => {
    if (!appreciationStudent || pending) return undefined;
    const stored = readPendingAward(api.storageKey);
    if (stored) { setPending(stored); setOpen(true); return undefined; }
    setKind('teacher_appreciation'); setIds([appreciationStudent.student_id]); setText('');
    setReview(true); setOpen(true); setError('');
    return undefined;
  }, [appreciationStudent, api, pending]);

  const close = () => {
    if (locked.current) return;
    setOpen(false); onAppreciationClose?.(); onFlowClose?.();
  };
  const closeSuccess = () => { setSuccess(null); onFlowClose?.(); };
  const startBonus = () => {
    const stored = readPendingAward(api.storageKey);
    if (stored) setPending(stored);
    if (!pending && !stored) {
      setKind('teacher_bonus'); setIds([]); setAmount(1); setText(''); setQuery(''); setSearchOpen(false); setReview(false); setError('');
    }
    setOpen(true);
  };
  const submit = async () => {
    if (locked.current || !chosenIds.length) return;
    if (appreciation) prepareAppreciationSound();
    else if ([1, 2].includes(points)) prepareBonusSound();
    if (!lockAward(api.storageKey)) { setError('Bu puan işlemi başka bir girişte doğrulanıyor. Sonucunu bekleyin.'); return; }
    locked.current = true; setBusy(true); setError('');
    let body = readPendingAward(api.storageKey) || pending;
    if (body) setPending(body);
    try {
      if (!body) {
        body = { operation_id: window.crypto.randomUUID(), kind, student_ids: ids,
          amount: points, ...(appreciation ? { note: quickAppreciation ? null : text.trim() || null } : { activity_title: text.trim() || null }) };
        // A storage failure prevents sending; otherwise a reload could create a second award.
        if (quickAppreciation && selecting) setSubmittingSelection(true);
        savePendingAward(api.storageKey, body); setPending(body);
      }
      const result = verifyAwardResponse(body, await api.submit(body));
      sessionStorage.removeItem(api.storageKey);
      setPending(null); setOpen(false); onAppreciationClose?.();
      setSuccess({ kind: body.kind, amount: body.amount, students: body.student_ids.map(id => students.find(s => s.student_id === id)
        || { student_id: id, first_name: 'Öğrenci', last_name: `#${id}` }), count: result.students.length });
      onAward();
    } catch (err) {
      const rejected = body?.kind === 'teacher_bonus' && body.amount === 3 && err.status === 422 && err.code === 'BONUS_AMOUNT_NOT_ALLOWED';
      setLegacyRejected(rejected);
      setError(rejected ? 'Eski +3 Bonus isteği kaydedilmedi. Yeni Bonus yalnız +1 veya +2 olabilir. Bu eski isteği iptal edebilirsiniz; iptal puan vermez.'
        : err.status === 401 ? 'Oturum süresi doldu. PIN / öğretmen girişi sonrasında aynı işlemi doğrulayın.'
        : 'Kayıt doğrulanamadı. Çift puan oluşmaması için aynı işlem kimliğiyle tekrar doğrulayın.');
    } finally { unlockAward(api.storageKey); locked.current = false; setBusy(false); setSubmittingSelection(false); }
  };
  const discardLegacy = () => {
    if (locked.current || !legacyRejected || !lockAward(api.storageKey)) return;
    try {
      const stored = readPendingAward(api.storageKey);
      if (stored?.operation_id !== pending?.operation_id) { setLegacyRejected(false); return; }
      sessionStorage.removeItem(api.storageKey);
      setPending(null); setLegacyRejected(false); setError(''); close();
    } catch { setError('Eski istek iptal edilemedi. Puan verilmedi; tekrar deneyin.'); }
    finally { unlockAward(api.storageKey); }
  };
  const visible = students.filter(s => name(s).toLocaleLowerCase('tr').includes(query.trim().toLocaleLowerCase('tr')));
  const selectedClass = ids.length ? students.find(s => s.student_id === ids[0])?.class_id : null;

  return <>
    {!hideLauncher && <div className={`pa-launch${boardProfile ? '' : ' pa-management'}`}>
      <Button variant="contained" onClick={startBonus} disabled={!students.length && !pending}>⭐ Öğretmen Bonusu</Button>
      {pending && <Alert severity="info" action={<Button onClick={() => setOpen(true)}>Doğrula</Button>}>Sonucu bekleyen bir puan işlemi var. Yeni işlemden önce doğrulayın.</Alert>}
    </div>}
    <Dialog open={open} onClose={close} fullWidth maxWidth="md" PaperProps={{ className: `pa-dialog${boardProfile ? '' : ' pa-management'}${selecting ? ' pa-selecting' : ''}` }} aria-labelledby="pa-title">
      <DialogTitle id="pa-heading" className="pa-heading"><span id="pa-title">{appreciation ? '👏' : '⭐'} {label}</span>
        {selecting && <Button className="pa-search-toggle" ref={searchToggle} disabled={busy} aria-label={searchOpen ? 'Öğrenci aramasını kapat' : 'Öğrenci aramasını aç'}
          aria-expanded={searchOpen} aria-controls={searchOpen ? 'pa-search' : undefined} onClick={() => searchOpen ? closeSearch() : setSearchOpen(true)}>
          {searchOpen ? <CloseIcon /> : <SearchIcon />}
        </Button>}
      </DialogTitle>
      <DialogContent className={`pa-content${selecting ? ' pa-selection' : ''}`} aria-busy={busy}>
        {pending && !submittingSelection && <Alert severity="info">Bekleyen işlem korunuyor. Aynı kimlikle yeniden gönderildiğinde ikinci kez puan verilmez.</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
        {appreciation && !selecting ? <>
          <div className="pa-person"><StudentAvatar student={chosen[0] || appreciationStudent} large /><strong>{chosen[0] ? name(chosen[0]) : ''}</strong><span>+3 puan</span></div>
          <p>Öğrencinizi özel bir nedenle takdir edin.</p>
          {!quickAppreciation && <TextField fullWidth multiline minRows={2} label="Kısa not (isteğe bağlı)" value={pending?.note ?? text}
            disabled={Boolean(pending)} inputProps={{ maxLength: 240 }} onChange={e => setText(e.target.value)}
            helperText="En fazla 240 karakter. Not, veli/öğrenci geçmişinde görünür; sınıf kutlamasında gösterilmez." />}
        </> : <>
          {selecting && <>
            {!students.length && <Alert severity="info">Bu sınıfta {appreciation ? 'takdir' : 'bonus'} verilecek öğrenci bulunmuyor. Pencereyi kapatıp daha sonra tekrar deneyebilirsiniz.</Alert>}
            {appreciation && <>
              <p>Takdir etmek istediğiniz öğrenciyi seçin. Öğretmen Takdiri +3 puandır.</p>
            </>}
            {searchOpen && <TextField id="pa-search" className="pa-search-field" inputRef={searchInput} disabled={busy} fullWidth label={`${appreciation ? 'Takdir' : 'Bonus'} için öğrenci ara`} value={query} onChange={e => setQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); closeSearch(); } }} />}
            {!appreciation && <span className="pa-sr-only" role="status">{ids.length} öğrenci seçili · Kişi başı +{amount} · Toplam {ids.length * amount} puan</span>}
            <div className="pa-students" aria-label={appreciation ? 'Takdir öğrencileri' : 'Bonus öğrencileri'}>
              {visible.map(student => <button key={student.student_id} type="button" className="pa-student" aria-pressed={ids.includes(student.student_id)}
                aria-label={`${name(student)} ${ids.includes(student.student_id) ? '✓ Seçili' : 'Seç'}`}
                disabled={busy || (!appreciation && selectedClass != null && student.class_id !== selectedClass)}
                onClick={() => setIds(previous => previous.includes(student.student_id) ? previous.filter(id => id !== student.student_id) : appreciation ? [student.student_id] : [...previous, student.student_id])}>
                <StudentAvatar student={student} /><strong>{name(student)}</strong>
                {ids.includes(student.student_id) && <span className="pa-selected-check" aria-hidden="true">✓</span>}
              </button>)}
              {!visible.length && Boolean(students.length) && <p role="status">Bu aramada öğrenci bulunamadı.</p>}
            </div>
            {!appreciation && <>
              <fieldset className="pa-amount"><legend>Kişi başına bonus</legend>{[1, 2].map(value => <Button key={value} variant={amount === value ? 'contained' : 'outlined'} aria-pressed={amount === value} onClick={() => setAmount(value)}>+{value}</Button>)}</fieldset>
              <TextField fullWidth label="Etkinlik adı (isteğe bağlı)" inputProps={{ maxLength: 120 }} value={text} onChange={e => setText(e.target.value)} />
            </>}
          </>}
          {(review || pending) && !selecting && <>
            <p className="pa-summary"><strong>{chosenIds.length} öğrenci</strong> × <strong>+{points} puan</strong> = <strong>{chosenIds.length * points} puan</strong></p>
            <p>Her öğrenci aynı miktarda Öğretmen Bonusu alacak.</p>
            <ul className="pa-selected">{chosen.map(s => <li key={s.student_id}>{name(s)} <strong>+{points}</strong></li>)}</ul>
            {(pending?.activity_title || text) && <p className="pa-text">Etkinlik: {pending?.activity_title || text}</p>}
          </>}
        </>}
      </DialogContent>
      <DialogActions className="pa-actions">
        <Button onClick={close} disabled={busy}>{pending && !submittingSelection ? 'Kapat' : 'Vazgeç'}</Button>
        {legacyRejected && <Button onClick={discardLegacy} disabled={busy}>Eski +3 Bonus isteğini iptal et</Button>}
        {!appreciation && review && !pending && <Button onClick={() => setReview(false)}>Seçimi düzenle</Button>}
        {!appreciation && !review && !pending ? <Button variant="contained" disabled={!ids.length} onClick={() => setReview(true)}>Seçimi kontrol et ({ids.length})</Button>
          : <Button variant="contained" disabled={busy || !chosenIds.length || legacyRejected} onClick={submit}>{busy ? 'Doğrulanıyor…' : pending ? 'Aynı işlemi tekrar doğrula' : appreciation ? quickAppreciation ? 'Tamam' : 'Takdiri ver · +3' : 'Bonusu ver'}</Button>}
      </DialogActions>
    </Dialog>
    <Dialog open={Boolean(success)} onClose={closeSuccess} fullWidth maxWidth={success?.kind === 'teacher_bonus' && !modernBonusSuccess ? 'sm' : 'md'}
      className={success?.kind === 'teacher_appreciation' ? 'pa-appreciation-overlay' : modernBonusSuccess ? 'pa-bonus-v2-overlay' : undefined}
      PaperProps={{ className: `pa-dialog pa-celebration${success?.kind === 'teacher_appreciation' ? ' pa-appreciation' : modernBonusSuccess ? ' pa-bonus-v2' : ''}${boardProfile ? '' : ' pa-management'}` }} aria-labelledby="pa-success-title"
      aria-describedby={success?.kind === 'teacher_appreciation' ? 'pa-appreciation-heading pa-appreciation-message pa-appreciation-saved' : modernBonusSuccess ? 'pa-bonus-saved' : undefined}>
      <DialogContent>
        {success?.kind === 'teacher_appreciation' ? <TeacherAppreciationCelebration student={success.students[0]} />
          : modernBonusSuccess ? <TeacherBonusCelebration count={success.count} amount={success.amount} />
          : success?.kind === 'teacher_bonus' ? <div role="status"><h2 id="pa-success-title">✓ Öğretmen Bonusu kaydedildi</h2><p>{success.count} öğrenciye kişi başı +{success.amount} puan</p></div> : null}
      </DialogContent>
      <DialogActions className="pa-actions"><Button autoFocus variant="contained" onClick={closeSuccess}>Tamam</Button></DialogActions>
    </Dialog>
  </>;
}
